/**
 * tool-call-view.ts — derive what a tool-call chip should display.
 *
 * Extracted from ToolCallCard because a rehydrated tool call is not shaped
 * like a live one, and the renderer assumed it was.
 *
 *   live (WebSocket):  { tool, input, result }            — all present
 *   rehydrated (DB):   { tool, summary, result?, resultOmitted?, ... }
 *
 * `input` is never persisted (it passes through no guardrail on the write
 * side), and `result` is absent for every tool not on the persistence
 * allowlist — which is most of them. The old inline logic dereferenced both
 * unguarded:
 *
 *   Object.keys(call.input).length   → TypeError on every rehydrated expand
 *   JSON.parse(call.result) → catch → call.result.length
 *                                    → a throw from INSIDE the catch handler,
 *                                      which nothing catches, unmounting the
 *                                      message tree in React 19
 *
 * Both fire on the normal path, so this is pure logic in its own module with
 * its own tests rather than a guard bolted onto JSX.
 */

export interface ToolCallLike {
  tool?: unknown;
  input?: unknown;
  result?: unknown;
  /** Computed at write time; the only label available once a result is dropped. */
  summary?: unknown;
  /** Tool was not on the persistence allowlist — payload deliberately absent. */
  resultOmitted?: unknown;
  /** Result was shortened. Still valid JSON when it started as JSON. */
  resultTruncated?: unknown;
  isError?: unknown;
}

export interface ToolCallView {
  /** Always a non-empty string. `tool` is dereferenced on the COLLAPSED chip,
   *  before any interaction, and arrives unvalidated from the rooms adapter
   *  (`tc.name ?? 'tool'` — ?? does not stop a number) and raw from the
   *  WebSocket. Validating it here rather than per-caller is what makes this
   *  module's "never throws" contract actually true. */
  tool: string;
  summary: string;
  detail: string;
  hasInput: boolean;
  omitted: boolean;
  truncated: boolean;
  isError: boolean;
}

const MAX_CHIP_SUMMARY = 80;

function deriveSummaryFromResult(result: string): string {
  try {
    const parsed = JSON.parse(result);
    if (Array.isArray(parsed)) return `${parsed.length} result${parsed.length === 1 ? '' : 's'} returned`;
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      if (typeof o.summary === 'string') return o.summary;
      if (typeof o.message === 'string') return o.message;
      if (o.status !== undefined) return `Status: ${String(o.status)}`;
      const keys = Object.keys(o);
      return `${keys.length} field${keys.length === 1 ? '' : 's'}: ${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '…' : ''}`;
    }
    return String(parsed);
  } catch {
    return result.length <= MAX_CHIP_SUMMARY ? result : `${result.slice(0, MAX_CHIP_SUMMARY - 3)}...`;
  }
}

/**
 * Total — never throws for any input shape. A tool-call chip must not be able
 * to take down the conversation it appears in.
 */
export function deriveToolCallView(call: ToolCallLike): ToolCallView {
  const omitted = call?.resultOmitted === true;
  const truncated = call?.resultTruncated === true;
  const isError = call?.isError === true;

  // Persisted summary wins. It is what the write side computed, and for a
  // dropped result it is the ONLY thing available — so preferring it keeps
  // the label stable whether or not the payload survived.
  const stored = typeof call?.summary === 'string' ? call.summary.trim() : '';
  const result = typeof call?.result === 'string' ? call.result : '';

  let summary = stored;
  if (!summary && result) summary = deriveSummaryFromResult(result);
  if (!summary) summary = omitted ? 'result not stored' : '—';

  let detail: string;
  if (omitted) {
    detail = 'Result not stored. This tool can return credentials, filesystem contents or another tenant’s data, so its output is deliberately not persisted.';
  } else if (!result) {
    detail = 'No result recorded.';
  } else {
    try {
      detail = JSON.stringify(JSON.parse(result), null, 2);
    } catch {
      detail = result;
    }
    if (truncated) detail += '\n\n[result truncated]';
  }

  const hasInput =
    !!call?.input && typeof call.input === 'object' && Object.keys(call.input as object).length > 0;

  const tool = typeof call?.tool === 'string' && call.tool.trim() ? call.tool : 'tool';

  // One clamp at the exit covers stored, derived AND fallback summaries. The
  // per-branch cap missed the JSON path entirely, so `{"summary":"A".repeat(1e6)}`
  // produced a megabyte-long React text node that CSS truncation merely hid.
  if (summary.length > MAX_CHIP_SUMMARY) summary = `${summary.slice(0, MAX_CHIP_SUMMARY - 3)}...`;

  return { tool, summary, detail, hasInput, omitted, truncated, isError };
}

/**
 * Is this safe to use as an href?
 *
 * Sources come from web search — attacker-controlled content. Persisting them
 * turns a transient reflected risk into a stored one, so the scheme is parsed
 * rather than pattern-matched: a regex on the raw string is defeated by
 * embedded control characters (`java\tscript:`) and leading whitespace, both
 * of which the DOM normalises away before navigating.
 */
export function isSafeHttpUrl(url: unknown): boolean {
  if (typeof url !== 'string' || url.trim() === '') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    // Relative or malformed — not a link we should render.
    return false;
  }
}

/**
 * Is this safe as a media src (img/video/audio/anchor) in rendered markdown?
 *
 * Broader than isSafeHttpUrl because generated media legitimately arrives as
 * data:/blob:. Everything else — javascript:, vbscript:, file:, and
 * protocol-relative — is refused. Parsed, not pattern-matched, for the same
 * reason as isSafeHttpUrl.
 */
export function isSafeMediaSrc(url: unknown): boolean {
  if (typeof url !== 'string' || url.trim() === '') return false;
  const s = url.trim();

  /**
   * Root-relative, same-origin paths.
   *
   * `new URL(s)` below THROWS on a relative path, so these returned false —
   * which meant `generate_image`'s own output was rejected. It emits
   * `![name](/api/artifacts/<id>/content)`, so the image fell through to the
   * raw-text branch and the user saw the markdown source instead of a picture.
   *
   * Same-origin is the SAFEST source here, not the riskiest: it cannot beacon
   * to a third party, which `https:` (already allowed) can.
   *
   * The `!startsWith('//')` guard is load-bearing. `//evil.com/x.png` is
   * PROTOCOL-RELATIVE and resolves cross-origin — it looks root-relative but
   * is not, so a bare `startsWith('/')` would open exactly the hole this
   * function exists to close.
   */
  if (s.startsWith('/') && !s.startsWith('//')) return true;

  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'data:' || u.protocol === 'blob:';
  } catch {
    return false;
  }
}
